import { supabase } from '../lib/supabase';

const dateKey = (ts) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dateLabel = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const buildAccountBucket = (accountId, name) => ({ id: accountId, name, total: 0, lastDate: null, _days: {} });

const finalizeAccounts = (buckets) =>
  buckets.map((acc) => {
    const days = Object.keys(acc._days)
      .sort()
      .map((key) => ({ date: key, label: dateLabel(key), count: acc._days[key] }));
    const lastDate = days.length ? days[days.length - 1].label : null;
    const { _days, ...rest } = acc;
    return { ...rest, lastDate, days };
  });

async function getPinterestWorkload(userId, startISO, profileId) {
  try {
    let accountsQuery = supabase.from('pinterest_accounts').select('id, username').eq('user_id', userId);
    let pinsQuery = supabase
      .from('pinterest_pins')
      .select('pinterest_account_id, scheduled_at, board_name')
      .eq('user_id', userId)
      .eq('status', 'scheduled')
      .gte('scheduled_at', startISO);
    if (profileId) {
      accountsQuery = accountsQuery.eq('profile_id', profileId);
      pinsQuery = pinsQuery.eq('profile_id', profileId);
    }

    const [{ data: accounts, error: accErr }, { data: pins, error: pinErr }] = await Promise.all([
      accountsQuery,
      pinsQuery
    ]);
    if (accErr) throw accErr;
    if (pinErr) throw pinErr;

    const buckets = {};
    (accounts || []).forEach((a) => {
      buckets[a.id] = buildAccountBucket(a.id, a.username || 'Pinterest Account');
    });

    (pins || []).forEach((p) => {
      const accId = p.pinterest_account_id;
      if (!accId) return;
      if (!buckets[accId]) buckets[accId] = buildAccountBucket(accId, p.board_name || 'Pinterest Account');
      const key = dateKey(p.scheduled_at);
      buckets[accId]._days[key] = (buckets[accId]._days[key] || 0) + 1;
      buckets[accId].total += 1;
    });

    return { accounts: finalizeAccounts(Object.values(buckets)) };
  } catch (err) {
    console.error('getPinterestWorkload:', err);
    return { accounts: [] };
  }
}

async function getFacebookWorkload(userId, startISO, profileId) {
  try {
    let pagesQuery = supabase.from('facebook_pages').select('page_id, page_name').eq('user_id', userId);
    let postsQuery = supabase
      .from('fb_scheduled_posts')
      .select('page_id, scheduled_at')
      .eq('user_id', userId)
      .eq('status', 'scheduled')
      .gte('scheduled_at', startISO);
    if (profileId) {
      pagesQuery = pagesQuery.eq('profile_id', profileId);
      // fb_scheduled_posts.profile_id is nullable (a handful of pre-existing
      // rows have no owner at all — see 20260801000004) so only scope when
      // a profileId is actually provided, same as the other tables here.
      postsQuery = postsQuery.eq('profile_id', profileId);
    }

    const [{ data: pages, error: pageErr }, { data: posts, error: postErr }] = await Promise.all([
      pagesQuery,
      postsQuery
    ]);
    if (pageErr) throw pageErr;
    if (postErr) throw postErr;

    const buckets = {};
    (pages || []).forEach((p) => {
      buckets[p.page_id] = buildAccountBucket(p.page_id, p.page_name || 'Facebook Page');
    });

    (posts || []).forEach((p) => {
      const pageId = p.page_id;
      if (!pageId) return;
      if (!buckets[pageId]) buckets[pageId] = buildAccountBucket(pageId, 'Facebook Page');
      const key = dateKey(p.scheduled_at);
      buckets[pageId]._days[key] = (buckets[pageId]._days[key] || 0) + 1;
      buckets[pageId].total += 1;
    });

    return { accounts: finalizeAccounts(Object.values(buckets)) };
  } catch (err) {
    console.error('getFacebookWorkload:', err);
    return { accounts: [] };
  }
}

async function getThreadsWorkload(userId, startISO, profileId) {
  try {
    let accountsQuery = supabase.from('threads_accounts').select('id, username, name').eq('user_id', userId);
    let postsQuery = supabase
      .from('threads_scheduled_posts')
      .select('account_id, scheduled_at')
      .eq('user_id', userId)
      .eq('status', 'scheduled')
      .gte('scheduled_at', startISO);
    if (profileId) {
      accountsQuery = accountsQuery.eq('profile_id', profileId);
      postsQuery = postsQuery.eq('profile_id', profileId);
    }

    const [{ data: accounts, error: accErr }, { data: posts, error: postErr }] = await Promise.all([
      accountsQuery,
      postsQuery
    ]);
    if (accErr) throw accErr;
    if (postErr) throw postErr;

    const buckets = {};
    (accounts || []).forEach((a) => {
      buckets[a.id] = buildAccountBucket(a.id, a.name || a.username || 'Threads Account');
    });

    (posts || []).forEach((p) => {
      const accId = p.account_id;
      if (!accId) return;
      if (!buckets[accId]) buckets[accId] = buildAccountBucket(accId, 'Threads Account');
      const key = dateKey(p.scheduled_at);
      buckets[accId]._days[key] = (buckets[accId]._days[key] || 0) + 1;
      buckets[accId].total += 1;
    });

    return { accounts: finalizeAccounts(Object.values(buckets)) };
  } catch (err) {
    console.error('getThreadsWorkload:', err);
    return { accounts: [] };
  }
}

export async function getScheduledWorkload(profileId = null) {
  const empty = { pinterest: { accounts: [] }, facebook: { accounts: [] }, threads: { accounts: [] } };
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return empty;

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const startISO = start.toISOString();

    const [pinterest, facebook, threads] = await Promise.all([
      getPinterestWorkload(user.id, startISO, profileId),
      getFacebookWorkload(user.id, startISO, profileId),
      getThreadsWorkload(user.id, startISO, profileId)
    ]);

    return { pinterest, facebook, threads };
  } catch (err) {
    console.error('getScheduledWorkload:', err);
    return empty;
  }
}
